import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { useModels } from '../../hooks/useModels';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Badge } from '../ui/badge';

interface ModelSelectorProps {
  value?: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  compact?: boolean;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({ value, onChange, disabled, compact = false }) => {
  const { availableModels, loading, error } = useModels();
  const [selectedModel, setSelectedModel] = useState<string>(value || '');

  // Sync with value prop
  useEffect(() => {
    console.log('🔄 ModelSelector: Syncing with value prop:', value);
    if (value) {
      setSelectedModel(value);
    }
  }, [value]);

  // Auto-select default model if none selected
  useEffect(() => {
    console.log('🔍 ModelSelector: Checking auto-select. selectedModel:', selectedModel, 'availableModels:', availableModels.length);
    console.log('📋 ModelSelector: Available models:', availableModels.map(m => ({ name: m.name, is_default: m.is_default })));
    if (!selectedModel && availableModels.length > 0) {
      // Try to find the default model
      let modelToSelect = availableModels.find(m => m.is_default);
      
      if (!modelToSelect) {
        console.log('❌ ModelSelector: No default model found, selecting first available model');
        // Fallback: select the first available model (they're already sorted by sort_order from backend)
        modelToSelect = availableModels[0];
      }
      
      if (modelToSelect) {
        console.log('✅ ModelSelector: Auto-selecting model:', modelToSelect.display_name, modelToSelect.model_id);
        setSelectedModel(modelToSelect.model_id);
        onChange(modelToSelect.model_id);
      }
    }
  }, [availableModels, selectedModel, onChange]);

  const handleChange = (modelId: string) => {
    setSelectedModel(modelId);
    onChange(modelId);
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading models...</div>;
  }

  if (error) {
    return <div className="text-sm text-red-500">Error loading models</div>;
  }

  return (
    <div className="space-y-2">
      <Label className={compact ? "text-xs" : ""}>AI Model</Label>
      <RadioGroup value={selectedModel} onValueChange={handleChange}>
        <div className={compact ? "space-y-2" : "space-y-3"}>
          {availableModels.map((model) => {
            const isAllowed = model.usage.allowed;
            const isLimited = model.usage.limit !== -1;

            return (
              <div
                key={model.model_id}
                className={`
                  ${compact ? 'p-2' : 'p-4'} border rounded-lg transition-all
                  ${selectedModel === model.model_id ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20' : 'border-border'}
                  ${!isAllowed ? 'opacity-60 bg-muted' : 'bg-background'}
                  ${isAllowed && !disabled ? 'cursor-pointer hover:border-blue-300' : 'cursor-not-allowed'}
                `}
                onClick={() => isAllowed && !disabled && handleChange(model.model_id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-2 flex-1">
                    <RadioGroupItem
                      value={model.model_id}
                      disabled={!isAllowed || disabled}
                      className="mt-0.5"
                    />
                    <div className="flex items-center space-x-2 flex-wrap flex-1">
                      <span className={`font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>{model.display_name}</span>
                      {model.badge_text && (
                        <Badge variant={model.badge_text === 'Premium' ? 'default' : 'secondary'} className={compact ? "text-[10px] px-1 py-0" : ""}>
                          {model.badge_text}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {model.credit_cost} credits
                    </span>
                  </div>
                </div>

                {!compact && model.description && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    {model.description}
                  </p>
                )}

                {isLimited && (
                  <div className={`flex items-center justify-end space-x-2 ${compact ? 'text-[10px] mt-1' : 'text-xs mt-2'} ml-6`}>
                    {!isAllowed && <Lock className="w-3 h-3 text-red-500" />}
                    <span className={!isAllowed ? 'text-red-500' : 'text-muted-foreground'}>
                      {model.usage.remaining}/{model.usage.limit} remaining this month
                    </span>
                  </div>
                )}

                {!isAllowed && (
                  <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-red-500 mt-1 ml-6`}>
                    Monthly limit reached. Upgrade for unlimited access.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </RadioGroup>
    </div>
  );
};
